"use client";

import React from "react";
import { Card } from "@/components/ui/card";
import { Loader2, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ProductVariant } from "@/hooks/useFunnelState";
import CoverageDetailItem from "./CoverageDetailItem";
import { ProductPlanDetails } from "@/types/product";
import { ScrollArea } from "@/components/ui/scroll-area"; // Import ScrollArea

interface PlanSummaryProps {
  selectedProduct: ProductVariant | null;
  currentFinalPrice: number | null;
  paymentInterval: 1 | 2 | 4 | 12;
  isLoading: boolean;
  yearlyDeductible: number;
  coPayCoveragePercentage: number;
  preexistingCoverage: boolean;
  worldwideCoverage: boolean;
  underwritingFailed?: boolean;
  underwritingReason?: string | null;
}

const PlanSummary: React.FC<PlanSummaryProps> = ({
  selectedProduct,
  currentFinalPrice,
  paymentInterval,
  isLoading,
  yearlyDeductible,
  coPayCoveragePercentage,
  preexistingCoverage,
  worldwideCoverage,
  underwritingFailed = false,
  underwritingReason = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  const getPaymentFrequencyText = (interval: 1 | 2 | 4 | 12) => {
    return t(`common.paymentIntervalOptions.${interval}`);
  };

  const monthlyPrice = currentFinalPrice !== null ? (currentFinalPrice * paymentInterval) / 12 : null;

  const getCoverageDetails = (details: ProductPlanDetails) => {
    const items = [];

    // 1. Max Yearly Vet Costs
    items.push({
      label: t("priceCustomization.table.maxYearlyVetCosts"),
      value: `CHF ${details.disease.sum_insured.toLocaleString('de-CH')}`,
      isCovered: details.disease.sum_insured > 0,
      hideIcon: true,
      isLabelBold: true,
    });

    // 2. Yearly Deductible (Moved up)
    items.push({
      label: t("common.yearlyDeductible"),
      value: `CHF ${yearlyDeductible}`,
      isCovered: true,
      hideIcon: true,
    });

    // 3. Co-Pay Coverage (Moved up)
    items.push({
      label: t("common.coPayCoverage"),
      value: `${coPayCoveragePercentage}%`,
      isCovered: true,
      hideIcon: true,
    });

    // 4. Dental Treatments
    const accidentCovered = details.disease.dental_treatment_accident > 0;
    const diseaseCovered = details.disease.dental_treatment_disease > 0;
    let dentalDetail: string = t("common.no");
    if (accidentCovered && diseaseCovered) {
      dentalDetail = t("priceCustomization.table.dentalAccidentDisease");
    } else if (accidentCovered) {
      dentalDetail = t("priceCustomization.table.dentalAccident");
    }
    items.push({
      label: t("priceCustomization.table.dentalTreatments"),
      value: dentalDetail,
      isCovered: accidentCovered || diseaseCovered,
    });

    // 5. Emergency Care
    items.push({
      label: t("priceCustomization.table.emergencyCare"),
      value: `CHF ${details.disease.emergency_care.toLocaleString('de-CH')}`,
      isCovered: details.disease.emergency_care > 0,
    });
    
    // 6. Behaviour Treatment
    items.push({
      label: t("priceCustomization.table.behaviourTreatment"),
      isCovered: details.disease.behaviour_treatment > 0,
      value: details.disease.behaviour_treatment > 0 ? t("common.yes") : t("common.no"),
    });

    // 7. Complementary Treatments
    items.push({
      label: t("priceCustomization.table.complementaryTreatments"),
      value: `CHF ${details.disease.alternative_treatment.toLocaleString('de-CH')}`,
      isCovered: details.disease.alternative_treatment > 0,
    });

    // 8. Preventive Treatments
    items.push({
      label: t("priceCustomization.table.preventiveTreatments"),
      value: details.disease.preventive_treatment > 0 ? `CHF ${details.disease.preventive_treatment.toLocaleString('de-CH')}` : t("common.no"),
      isCovered: details.disease.preventive_treatment > 0,
    });

    // 9. Medical Food
    items.push({
      label: t("priceCustomization.table.medicalFood"),
      value: details.disease.medical_food > 0 ? `${details.disease.medical_food}% (max. CHF ${details.disease.medical_food_cap.toLocaleString('de-CH')})` : t("common.no"),
      isCovered: details.disease.medical_food > 0,
    });

    // 10. Advanced Treatments
    items.push({
      label: t("priceCustomization.table.advancedTreatments"),
      isCovered: details.disease.advanced_treatment > 0,
      value: details.disease.advanced_treatment > 0 ? t("common.yes") : t("common.no"),
    });

    // Add-ons (These remain at the bottom)
    items.push({
      label: t("priceCustomization.preexistingConditions"),
      isCovered: preexistingCoverage,
      value: preexistingCoverage ? t("common.yes") : t("common.no"),
    });
    items.push({
      label: t("common.worldwideCoverage"),
      isCovered: worldwideCoverage,
      value: worldwideCoverage ? t("common.yes") : t("common.no"),
    });

    return items;
  };

  const coverageDetails = selectedProduct ? getCoverageDetails(selectedProduct.details) : [];
  const underwritingMessage = underwritingReason || t("underwritingError.defaultReason");

  return (
    <div className="w-full mb-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="overflow-hidden border-primary sticky top-2 z-20"> {/* Added sticky top-4 z-20 */}
          <CollapsibleTrigger asChild>
            <div className="p-6 cursor-pointer hover:bg-accent/50">
              <div className="flex justify-between items-start">
                <div className="flex-1 flex flex-col items-center text-center">
                  <h3 className="text-2xl font-bold">{selectedProduct?.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{selectedProduct?.description}</p>
                  <div className="text-center">
                    {isLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                    ) : underwritingFailed ? (
                      <p className="text-sm font-semibold text-destructive">
                        {underwritingMessage}
                      </p>
                    ) : monthlyPrice !== null ? (
                      <>
                        <p className="text-2xl font-bold text-destructive">
                          CHF {monthlyPrice.toFixed(2)} / {t("common.monthly")}
                        </p>
                        {paymentInterval !== 12 && currentFinalPrice !== null && (
                          <p className="text-sm text-muted-foreground">
                            CHF {currentFinalPrice.toFixed(2)} / {getPaymentFrequencyText(paymentInterval)}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-lg font-bold text-muted-foreground">-</p>
                    )}
                  </div>
                </div>
                <ChevronDown
                  className={cn("h-6 w-6 ml-4 transition-transform flex-shrink-0", isOpen && "rotate-180")}
                />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="max-h-[70vh] overflow-y-auto">
            <div className="p-4 border-primary">
                {coverageDetails.map((item) => (
                  <CoverageDetailItem
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    isCovered={item.isCovered}
                    hideIcon={item.hideIcon}
                    isLabelBold={item.isLabelBold}
                  />
                ))}
              </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default PlanSummary;
